import { EditOtherDistributionModal } from 'components/EditOtherDistributionModal';
import { limitsChoiceItems } from 'components/EditOtherDistributionModal/commonData';
import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';
import type {
    WorkOrderCostLimitDistributionData,
    WorkOrderOtherCostObjectItem,
    WorkOrderOtherPriceCostData,
} from 'gql/graphql';
import { OtherCostCalculationTypeEnum, WorkOrderStatusEnum } from 'gql/graphql';
import { tabItemsDistribution } from 'pages/Project/Common/types';
import { formatNumberToRu } from 'utils';
import { replacerCost } from 'utils/replacerCost';
import { sumFloatNumbers } from 'utils/sumFloatNumbers';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

import {
    getWorkOrderOtherForDistribution,
    gqlUpdateWorkOrderOtherCost,
    updateWorkOrderActOtherDistributionGql,
} from './EditOtherDistributionQueries';

export const EditOtherDistribution = ({
    onClose,
    workOrderId,
    currentConstructionTypeId,
    costRounding,
    workOrderStatus,
    defaultObjectId,
    otherId: otherIdProp,
}: {
    onClose: () => void;
    workOrderId: number;
    currentConstructionTypeId: number;
    costRounding: number;
    workOrderStatus: WorkOrderStatusEnum | undefined;
    defaultObjectId: number;
    otherId?: number;
}) => {
    const dispatch = useDispatch();

    const { otherId: otherIdParam } = useParams();

    const otherId = useMemo(() => otherIdProp ?? Number(otherIdParam), [otherIdParam, otherIdProp]);

    const workOrderOtherId = otherId;
    const {
        control,
        reset,
        handleSubmit,
        watch,
        clearErrors,
        setError,
        formState: { errors, isDirty },
        setValue,
    } = useForm();

    const allFormValues = watch();

    const [isOpenWarningDialog, setIsOpenWarningDialog] = useState<boolean>(false);
    const [activeTab, setActiveTab] = useState(tabItemsDistribution[0]);

    const otherDistributionVariables = useMemo(
        () => ({
            workOrderId,
            workOrderOtherPriceId: workOrderOtherId,
        }),
        [workOrderId, workOrderOtherId],
    );

    const [otherDistributionData, otherDistributionStatus] = useRequest({
        requestId: 'workOrderOtherForDistribution',
        query: getWorkOrderOtherForDistribution,
        withEffect: !!(workOrderId && workOrderOtherId),
        variables: otherDistributionVariables,
    });
    const [resetOtherDistributionData] = useResetStoreData({ requestId: 'workOrderOtherForDistribution' });
    const [resetUpdateCostData] = useResetStoreData({ requestId: 'updateOtherCostDistribution' });

    const successUpdateHandler = useCallback(() => {
        resetOtherDistributionData(undefined);
        resetUpdateCostData(undefined);
        onClose?.();
    }, [onClose, resetOtherDistributionData, resetUpdateCostData]);

    const [, updateCostStatus, , updateCostRequest] = useRequest({
        requestId: 'updateOtherCostDistribution',
        query: gqlUpdateWorkOrderOtherCost,
        withEffect: false,
        dependenciesUpdater: successUpdateHandler,
    });
    const [, updateActCostStatus, , updateActCostRequest] = useRequest({
        requestId: 'updateOtherCostDistribution',
        query: updateWorkOrderActOtherDistributionGql,
        withEffect: false,
        dependenciesUpdater: successUpdateHandler,
    });

    const isWorkOrderCostUpdating =
        updateCostStatus === RequestStatus.FETCHING || updateActCostStatus === RequestStatus.FETCHING;

    const isDataFetching = otherDistributionStatus === RequestStatus.FETCHING;

    const { workOrderObjsData, workOrderCommonData, workOrderLimitsData } = useMemo(
        () => ({
            workOrderCommonData: otherDistributionData?.uerWorkOrderOtherForDistribution?.data?.contractOtherCost,
            workOrderLimitsData: otherDistributionData?.uerWorkOrderOtherForDistribution?.data?.limits,
            workOrderObjsData: otherDistributionData?.uerWorkOrderOtherForDistribution?.data?.workOrderObjects,
        }),
        [
            otherDistributionData?.uerWorkOrderOtherForDistribution?.data?.contractOtherCost,
            otherDistributionData?.uerWorkOrderOtherForDistribution?.data?.limits,
            otherDistributionData?.uerWorkOrderOtherForDistribution?.data?.workOrderObjects,
        ],
    );

    const otherCostId = useMemo(() => workOrderCommonData?.id, [workOrderCommonData?.id]);
    const isCalculating = useMemo(
        () => workOrderCommonData?.calculatingType === OtherCostCalculationTypeEnum.Calculation,
        [workOrderCommonData?.calculatingType],
    );

    const isFormWithNoCheckedLimits = useMemo(() => {
        if (isCalculating) {
            return Object.values(
                (allFormValues?.includedCost ?? {}) as Record<string, string | null | undefined>,
            ).every((value) => !value || replacerCost(value) === 0);
        }

        return Object.values(
            (allFormValues?.isIncluded ?? {}) as Record<
                string,
                {
                    isIncluded: boolean;
                }
            >,
        ).every((value) => !value?.isIncluded);
    }, [allFormValues, isCalculating]);

    useEffect(() => {
        if (!errors.includedCost) {
            return;
        }
        const currentErrorIncludedCost = Object.keys(errors.includedCost).map((i) => `includedCost.${i}`);

        if (!isFormWithNoCheckedLimits) {
            clearErrors(...currentErrorIncludedCost);
        }
    }, [clearErrors, errors.includedCost, allFormValues, isFormWithNoCheckedLimits]);

    useEffect(() => {
        if (!errors.isIncluded) {
            return;
        }
        const currentErrorIsIncluded = Object.keys(errors.isIncluded).map((i) => `isIncluded.${i}`);

        if (!isFormWithNoCheckedLimits) {
            clearErrors(...currentErrorIsIncluded);
        }
    }, [clearErrors, errors.includedCost, allFormValues, errors.isIncluded, isFormWithNoCheckedLimits]);

    const linkedSeasonDistributionsData = useMemo<
        | {
              objects: WorkOrderOtherCostObjectItem[] | undefined;
              objectDistributionData: WorkOrderOtherPriceCostData[] | undefined;
              objectLimitsData: WorkOrderCostLimitDistributionData | undefined;
          }[]
        | undefined
    >(
        () =>
            otherDistributionData?.uerWorkOrderOtherForDistribution?.data?.limits?.map((limit) => {
                const currentSeasonId = limit?.season?.id;

                const seasonDistributionData =
                    otherDistributionData?.uerWorkOrderOtherForDistribution?.data?.objectSeasonDistributions
                        ?.filter((distribution) => distribution?.seasonId === currentSeasonId)
                        .map((o) => ({ ...o, isIncluded: defaultObjectId === o?.objectId ? true : o?.isIncluded }));

                const currentDistributionObjects =
                    otherDistributionData?.uerWorkOrderOtherForDistribution?.data?.workOrderObjects?.filter(
                        (object) => seasonDistributionData?.some((i) => i.objectId === object.workOrderRootObjectId),
                    );

                return {
                    objects: currentDistributionObjects,
                    objectDistributionData: seasonDistributionData,
                    objectLimitsData: limit,
                };
            }),
        [
            defaultObjectId,
            otherDistributionData?.uerWorkOrderOtherForDistribution?.data?.limits,
            otherDistributionData?.uerWorkOrderOtherForDistribution?.data?.objectSeasonDistributions,
            otherDistributionData?.uerWorkOrderOtherForDistribution?.data?.workOrderObjects,
        ],
    );

    useEffect(() => {
        if (workOrderLimitsData) {
            const preparedLimits = workOrderLimitsData.reduce(
                (
                    acc: {
                        costLimit: {
                            [key: number]: number;
                        };
                        totalRemainderCost: {
                            [key: number]: number;
                        };
                        includedCost: {
                            [key: number]: number;
                        };
                        isIncluded: {
                            [key: number]: (typeof limitsChoiceItems)[0];
                        };
                    },
                    item,
                ) => {
                    let isIncluded;

                    if (isCalculating) {
                        isIncluded = item.includedCost ? limitsChoiceItems[0] : limitsChoiceItems[1];
                    } else {
                        isIncluded = item.isIncluded ? limitsChoiceItems[0] : limitsChoiceItems[1];
                    }

                    acc.costLimit[item.season?.id] = formatNumberToRu(item.costLimit, 2);
                    acc.totalRemainderCost[item.season?.id] = formatNumberToRu(item.totalRemainderCost, 2);
                    acc.includedCost[item.season?.id] = formatNumberToRu(item.includedCost, 2);
                    acc.isIncluded[item.season?.id] = isIncluded;

                    return acc;
                },
                { costLimit: {}, totalRemainderCost: {}, includedCost: {}, isIncluded: {} },
            );

            reset({
                costLimit: preparedLimits.costLimit,
                totalRemainderCost: preparedLimits.totalRemainderCost,
                includedCost: preparedLimits.includedCost,
                isIncluded: preparedLimits.isIncluded,
            });
        }
    }, [isCalculating, reset, workOrderLimitsData, workOrderObjsData]);

    const handleCancel = useCallback(() => {
        if (isDirty) {
            setIsOpenWarningDialog(true);
        } else {
            onClose?.();
        }
    }, [isDirty, onClose]);

    const handleTabChange = useCallback(({ value }) => setActiveTab(value), [setActiveTab]);

    const onSubmit = useCallback(
        (formData) => {
            const seasonPrices = Object.entries(formData?.costLimit).reduce(
                (
                    acc: {
                        prices: {
                            seasonId: number;
                            isIncluded?: boolean;
                            includedCost?: number | null;
                        }[];
                        haveError: boolean;
                    },
                    [key],
                ) => {
                    if (isFormWithNoCheckedLimits) {
                        acc.haveError = true;
                    }

                    const seasonId = Number(key);

                    const seasonIncludedCostValue =
                        !formData.includedCost?.[key] || replacerCost(formData.includedCost?.[key]) === 0
                            ? null
                            : replacerCost(formData.includedCost?.[key]);

                    const seasonObj = {
                        seasonId,
                        ...(isCalculating && {
                            includedCost: seasonIncludedCostValue,
                            isIncluded: Boolean(seasonIncludedCostValue),
                        }),
                        ...(!isCalculating && {
                            isIncluded: allFormValues.isIncluded?.[key]?.isIncluded,
                            includedCost: seasonIncludedCostValue ?? 0,
                        }),
                        isSystemCalculation: formData?.objectDistribution?.find(
                            (d: { seasonId: number }) => d.seasonId === seasonId,
                        ).isSystemCalculation,
                    };

                    acc.prices.push(seasonObj);

                    return acc;
                },
                { prices: [], haveError: false },
            );

            if (seasonPrices.haveError) {
                return;
            }

            const objList = formData?.objectDistribution?.reduce(
                (
                    acc: {
                        objList: Array<{
                            cost: number;
                            objectId: number;
                            seasonId: number;
                        }>;
                        seasonSum: { [key: number]: number };
                        seasonHaveError: boolean;
                    },
                    item: {
                        seasonObjects: Array<{
                            cost: number;
                            isIncluded: boolean;
                            objectId: number;
                            seasonId: number;
                            hasErrors: boolean;
                        }>;
                    },
                ) => {
                    item.seasonObjects.forEach((i) => {
                        const itemSeason = i.seasonId;

                        if (i.hasErrors) {
                            acc.seasonHaveError = true;
                        }

                        if (i.isIncluded) {
                            acc.objList.push({
                                cost: replacerCost(i?.cost),
                                objectId: i?.objectId,
                                seasonId: itemSeason,
                            });

                            acc.seasonSum[itemSeason] = sumFloatNumbers(
                                acc.seasonSum[itemSeason] ?? 0,
                                replacerCost(i.cost ?? 0),
                            );
                        }
                    });

                    return acc;
                },
                {
                    objList: [],
                    seasonSum: {},
                    seasonHaveError: false,
                },
            );

            if (objList?.seasonHaveError) {
                setError('objectDistribution.0.isSystemCalculation', {}, { shouldFocus: true });
                setActiveTab(tabItemsDistribution[3]);

                return;
            }

            /* eslint-disable-next-line */
            for (const price of seasonPrices.prices) {
                const currentSeasonObjSum = objList.seasonSum[price?.seasonId] ? objList.seasonSum[price?.seasonId] : 0;

                if (price.isIncluded && price.includedCost !== currentSeasonObjSum) {
                    setError('objectDistribution.0.isSystemCalculation', {}, { shouldFocus: true });

                    setActiveTab(tabItemsDistribution[3]);

                    return;
                }
                clearErrors('objectDistribution.0.isSystemCalculation');
            }

            const updateVariables = {
                workOrderOtherPriceId: Number(otherId),
                updateInput: {
                    objectList: objList.objList,
                    otherCostId,
                    otherSeasonPrices: seasonPrices.prices,
                },
            };

            if (workOrderStatus === WorkOrderStatusEnum.OnFormation) {
                dispatch(updateCostRequest(updateVariables));
            }
            if (workOrderStatus === WorkOrderStatusEnum.InProgress) {
                dispatch(updateActCostRequest(updateVariables));
            }
        },
        [
            otherId,
            otherCostId,
            isFormWithNoCheckedLimits,
            isCalculating,
            allFormValues.isIncluded,
            clearErrors,
            setError,
            workOrderStatus,
            dispatch,
            updateCostRequest,
            updateActCostRequest,
        ],
    );

    const handleSubmitWarningDialog = useCallback(() => {
        setIsOpenWarningDialog(false);
        onClose?.();
    }, [onClose]);

    const handleCancelWarningDialog = useCallback(() => setIsOpenWarningDialog(false), []);

    return (
        <EditOtherDistributionModal
            handleCancel={handleCancel}
            onSubmit={onSubmit}
            isLoading={isWorkOrderCostUpdating}
            activeTab={activeTab}
            handleTabChange={handleTabChange}
            isDataFetching={isDataFetching}
            isCalculating={isCalculating}
            workOrderCommonData={workOrderCommonData}
            currentConstructionTypeId={currentConstructionTypeId}
            workOrderLimitsData={workOrderLimitsData}
            isOpenWarningDialog={isOpenWarningDialog}
            handleSubmitWarningDialog={handleSubmitWarningDialog}
            handleCancelWarningDialog={handleCancelWarningDialog}
            formControl={control}
            formHandleSubmit={handleSubmit}
            uerWorkOrderOtherDistributionData={linkedSeasonDistributionsData}
            costRounding={costRounding}
            workOrderOtherPriceId={workOrderOtherId}
            watch={watch}
            setValue={setValue}
        />
    );
};

EditOtherDistribution.displayName = 'EditOtherDistribution';
